import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { useLazyGetBranchByCodeQuery } from '@/features/local-configuration/api/local-configuration-api';
import {
  logout as logoutAction,
  selectBranchDetail,
  selectIsAuthenticated,
  selectUser,
  setBranchDetail,
  setSession,
} from '@/features/auth/store/auth-slice';
import type {
  Pro20User,
  SignInWithUserCodeRequest,
} from '@/features/auth/types/auth.types';
import { resolveBranchCode } from '@/features/auth/utils/resolve-branch-code';
import { baseApi } from '@/shared/api/base-api';

import { useSignInWithUserCodeMutation } from '../api/auth-api';

async function fetchBranchInBackground(
  branchCode: string,
  getBranch: ReturnType<typeof useLazyGetBranchByCodeQuery>[0],
  dispatch: ReturnType<typeof useAppDispatch>,
): Promise<void> {
  try {
    const branch = await getBranch(branchCode).unwrap();
    dispatch(setBranchDetail(branch));
  } catch {
    // Branch fetch is non-blocking — dashboard can still load
  }
}

export function useAuth() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const branchDetail = useAppSelector(selectBranchDetail);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [signInWithUserCode, userCodeState] = useSignInWithUserCodeMutation();
  const [getBranch] = useLazyGetBranchByCodeQuery();

  const persistSession = useCallback(
    (result: Pro20User, rememberMe?: boolean) => {
      const branchCode = resolveBranchCode(result);

      dispatch(
        setSession({
          user: result,
          accessToken: result.jwtToken,
          branchCode,
          branches: result.branches ?? [],
          loginMode: 'userCode',
          rememberMe,
        }),
      );

      if (branchCode) {
        void fetchBranchInBackground(branchCode, getBranch, dispatch);
      }
    },
    [dispatch, getBranch],
  );

  const loginWithUserCode = useCallback(
    async (credentials: SignInWithUserCodeRequest) => {
      const result = await signInWithUserCode(credentials).unwrap();
      persistSession(result, credentials.rememberMe);
      return result;
    },
    [persistSession, signInWithUserCode],
  );

  const logout = useCallback(() => {
    dispatch(logoutAction());
    // Drop cached data and subscriptions so the next session starts clean.
    dispatch(baseApi.util.resetApiState());
  }, [dispatch]);

  return {
    user,
    branchDetail,
    isAuthenticated,
    loginWithUserCode,
    logout,
    isLoggingIn: userCodeState.isLoading,
    loginError: userCodeState.error,
  };
}
